import {
  Snackbar,
  Stack,
  Card,
  Autocomplete,
  Checkbox,
  TextField,
  Skeleton,
  InputAdornment,
  IconButton,
  MenuItem,
} from "@mui/material";
import {
  ProtocolInfoOut,
  useProtocolApiV1ListProtocolsQuery,
  useProtocolApiV1TreatmentSiteDataQuery,
  useWebApiV1CreateSiteGroupMutation,
  useWebApiV1ListSiteGroupsQuery,
} from "@providers/hop-ord-server/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import ProtocolList from "./ProtocolList";
import SiteNameModal from "@pages/Order/components/SiteNameModal/SiteNameModal";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { protocolSearch, ProtocolResult } from "./protocolSearch";
import ClearIcon from "@mui/icons-material/Clear";

export interface ProtocolPanelProps {
  isReadOnly: boolean;
  orderId: number;
  tenantId: number;
}

const ProtocolSearchFilterSkeleton = () => {
  return (
    <Stack direction={{ sm: "column", md: "row" }} gap={1}>
      <Skeleton
        variant="rounded"
        height="48px"
        sx={{
          width: { sm: "100%", md: "30%" },
        }}
      />
      <Skeleton
        variant="rounded"
        height="48px"
        sx={{
          width: { sm: "100%", md: "70%" },
        }}
      />
    </Stack>
  );
};

const ProtocolPanel = (props: ProtocolPanelProps) => {
  const [filteredProtocols, setFilteredProtocols] = useState<ProtocolInfoOut[]>(
    [],
  );
  const [showAddSite, setShowAddSite] = useState<boolean>(false);
  const [creatingSiteGroup, setCreatingSiteGroup] = useState<
    number | undefined
  >(undefined);
  const [filters, setFilters] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [hasExactMatches, setHasExactMatches] = useState(true);
  const [hasSimilarMatches, setHasSimilarMatches] = useState(true);
  const [addedAlertOpen, setAddedAlertOpen] = useState(false);
  const [addedProtocolName, setAddedProtocolName] = useState("");
  const [newSiteGroupCreated, setNewSiteGroupCreated] = useState(false);
  const [createSiteGroupMutation] = useWebApiV1CreateSiteGroupMutation();
  const [searchAndFilteredProtocols, setSearchAndFilteredProtocols] = useState<
    ProtocolResult[]
  >([]);
  const { data: siteGroups, refetch: refetchSiteGroups } =
    useWebApiV1ListSiteGroupsQuery({
      orderId: props.orderId,
    });

  const { data: protocolData, isLoading } = useProtocolApiV1ListProtocolsQuery({
    limit: 1000,
    offset: 0,
    tenantId: props.tenantId,
  });

  /** Treatment site query */
  const { data: treatmentSiteData, isLoading: treatmentSiteLoading } =
    useProtocolApiV1TreatmentSiteDataQuery();

  const showSkeleton = treatmentSiteLoading || isLoading;

  const treatmentSiteOptions =
    treatmentSiteData?.map((site) => site.name) ?? [];

  const protocols = useMemo(() => {
    if (!protocolData) return [];
    return protocolData.items;
  }, [protocolData]);

  // Effect to handle scrolling to new site group
  useEffect(() => {
    if (newSiteGroupCreated && siteGroups?.length) {
      // Add a delay to allow the component to be created
      setTimeout(() => {
        const siteGroupPanel = document.getElementById(
          `site-group-panel-${siteGroups?.length - 1}`,
        );

        if (siteGroupPanel) {
          siteGroupPanel.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        setNewSiteGroupCreated(false);
      }, 700);
    }
  }, [siteGroups, newSiteGroupCreated]);

  // Create Site Group Function
  const createSiteGroup = (
    protocolId?: number,
    name?: string,
    nameSpecify?: string,
  ) => {
    if (props.isReadOnly) {
      return Promise.reject("read only");
    }
    return createSiteGroupMutation({
      orderId: Number(props.orderId),
      protocolId,
      manualSiteName: name,
      manualSiteSpecify: nameSpecify,
    });
  };

  const addProtocol = (
    protocol?: ProtocolInfoOut,
    name?: string,
    nameSpecify?: string,
  ) => {
    if (props.isReadOnly) {
      return;
    }
    setCreatingSiteGroup(protocol?.id);
    createSiteGroup(protocol?.id, name, nameSpecify)
      .then(() => {
        setAddedProtocolName(protocol?.name ?? "Manual Site Group");
        setAddedAlertOpen(true);
        setNewSiteGroupCreated(true);
        refetchSiteGroups();
      })
      .finally(() => {
        setCreatingSiteGroup(undefined);
      });
  };

  /* 
    Filter & Search:
    - First update the protocols list based on selected filters (filteredProtocols)
    - Second update the filteredProtocols list based on search term (searchAndFilteredProtocols)
    If no filters and no search term then return all results
  */
  const updateFilteredProtocols = useCallback(
    (selectedFilters: string[]) => {
      let filters = selectedFilters;

      // If no filters are selected, return all results
      if (!selectedFilters.length) {
        const tumourStreams = new Set(
          protocols.flatMap((p: ProtocolInfoOut) => p.tumourStreams),
        );
        filters = [...tumourStreams.keys()].sort();
      }
      setFilteredProtocols(
        protocols.filter((p) =>
          p.tumourStreams.some((stream: string) => filters.includes(stream)),
        ),
      );
    },
    [protocols],
  );

  useEffect(() => {
    const { hasExactMatches, hasSimilarMatches, results } = protocolSearch(
      searchText,
      filteredProtocols,
    );
    setHasExactMatches(hasExactMatches);
    setHasSimilarMatches(hasSimilarMatches);
    setSearchAndFilteredProtocols(results);
  }, [filteredProtocols, searchText]);

  // get unique tumour streams
  useEffect(() => {
    const tumourStreams = new Set(
      protocols.flatMap((p: ProtocolInfoOut) => p.tumourStreams),
    );
    const newFilters = [...tumourStreams.keys()].sort();
    setFilters(newFilters);
    updateFilteredProtocols(newFilters);
  }, [protocols, updateFilteredProtocols]);

  return (
    <Stack>
      <Card sx={{ p: 2 }}>
        <Stack>
          {showSkeleton ? (
            <ProtocolSearchFilterSkeleton />
          ) : (
            <Stack
              direction={{ sm: "column", md: "row" }}
              alignItems="center"
              gap={2}
            >
              <TextField
                id="protocol-search"
                label="Search"
                variant="outlined"
                margin="normal"
                value={searchText}
                sx={{
                  width: { sm: "100%", md: "30%" },
                  marginTop: 0,
                  marginBottom: 0,
                }}
                slotProps={{
                  input: {
                    placeholder: "Protocol name",
                    endAdornment: searchText && (
                      <InputAdornment position="end" sx={{ width: "30px" }}>
                        <IconButton
                          onClick={() => {
                            setSearchText("");
                          }}
                        >
                          {<ClearIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchText(event.target.value);
                }}
              />
              <Autocomplete
                multiple
                data-testid="protocols-filter"
                options={filters}
                limitTags={2}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <MenuItem {...props} selected={selected}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      sx={{ marginRight: 1 }}
                      checked={selected}
                    />
                    {option}
                  </MenuItem>
                )}
                sx={{
                  width: { sm: "100%", md: "70%" },
                }}
                slotProps={{
                  listbox: {
                    sx: { maxHeight: 364 },
                  },
                  popper: {
                    sx: { zIndex: 99 },
                  },
                }}
                onChange={(_, value) => {
                  updateFilteredProtocols([...value]);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter"
                    placeholder="Tumor streams"
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
              />
            </Stack>
          )}

          <SiteNameModal
            open={showAddSite}
            options={treatmentSiteOptions}
            onSubmit={(name, nameSpecify) => {
              setShowAddSite(false);
              const manualProtocol = undefined;
              addProtocol(manualProtocol, name, nameSpecify);
            }}
            onDismiss={() => {
              setShowAddSite(false);
            }}
          />
        </Stack>
        <ProtocolList
          protocols={searchAndFilteredProtocols}
          isLoading={showSkeleton}
          isReadOnly={props.isReadOnly ?? false}
          addProtocol={addProtocol}
          setShowAddSite={setShowAddSite}
          hasExactMatches={hasExactMatches}
          hasSimilarMatches={hasSimilarMatches}
          showButtonLoadingSpinner={creatingSiteGroup}
        />
        <Snackbar
          open={addedAlertOpen}
          onClose={() => setAddedAlertOpen(false)}
          autoHideDuration={2000}
          message={`Planning protocol added: ${addedProtocolName}`}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Card>
    </Stack>
  );
};

export default ProtocolPanel;
